import { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { deleteFoodItemFromDatabase } from '../data/AsyncFunctions/deleteFoodItemFromDatabase';
import {
  getAdjustedExpirationDate,
  shouldDeleteForQuantity,
} from '../data/AsyncFunctions/foodItemStorageRules';
import { splitFoodItemInDatabase } from '../data/AsyncFunctions/splitFoodItemInDatabase';
import { updateFoodItemInDatabase } from '../data/AsyncFunctions/updateFoodItemInDatabase';
import { useAuth } from '../contexts/authContext';

const foodImagesContext = require.context(
  '../data/food_images',
  false,
  /\.(png|jpe?g|webp|svg)$/i
);

function normalizeName(value = '') {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

const foodImageLibrary = foodImagesContext.keys().map((imagePath) => {
  const fileName = imagePath.replace('./', '').replace(/\.[^/.]+$/, '');

  return {
    normalizedName: normalizeName(fileName),
    src: foodImagesContext(imagePath),
  };
});

function getLevenshteinDistance(source = '', target = '') {
  const rows = source.length + 1;
  const columns = target.length + 1;
  const matrix = Array.from({ length: rows }, () => Array(columns).fill(0));

  for (let row = 0; row < rows; row += 1) {
    matrix[row][0] = row;
  }

  for (let column = 0; column < columns; column += 1) {
    matrix[0][column] = column;
  }

  for (let row = 1; row < rows; row += 1) {
    for (let column = 1; column < columns; column += 1) {
      const substitutionCost = source[row - 1] === target[column - 1] ? 0 : 1;

      matrix[row][column] = Math.min(
        matrix[row - 1][column] + 1,
        matrix[row][column - 1] + 1,
        matrix[row - 1][column - 1] + substitutionCost
      );
    }
  }

  return matrix[source.length][target.length];
}

function getBestFoodImage(name = '') {
  const normalizedTarget = normalizeName(name);

  if (!normalizedTarget) {
    return null;
  }

  const exactMatch = foodImageLibrary.find(
    (image) => image.normalizedName === normalizedTarget
  );

  if (exactMatch) {
    return exactMatch.src;
  }

  const includesMatch = foodImageLibrary.find(
    (image) =>
      image.normalizedName.includes(normalizedTarget) ||
      normalizedTarget.includes(image.normalizedName)
  );

  if (includesMatch) {
    return includesMatch.src;
  }

  let closestImage = null;
  let closestScore = Number.POSITIVE_INFINITY;

  for (const image of foodImageLibrary) {
    const score = getLevenshteinDistance(normalizedTarget, image.normalizedName);

    if (score < closestScore) {
      closestScore = score;
      closestImage = image;
    }
  }

  if (!closestImage) {
    return null;
  }

  const similarityThreshold = Math.max(
    2,
    Math.floor(Math.max(normalizedTarget.length, closestImage.normalizedName.length) / 3)
  );

  return closestScore <= similarityThreshold ? closestImage.src : null;
}

function getDaysUntilExpiration(expirationDate) {
  if (!expirationDate) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiration = new Date(expirationDate);
  expiration.setHours(0, 0, 0, 0);

  if (Number.isNaN(expiration.getTime())) {
    return null;
  }

  return Math.round((expiration - today) / (1000 * 60 * 60 * 24));
}

function getExpirationStyle(daysUntilExpiration) {
  if (daysUntilExpiration === null) {
    return {
      borderColor: '#94a3b8',
      badgeColor: '#475569',
      statusLabel: 'No date',
    };
  }

  if (daysUntilExpiration < 0) {
    return {
      borderColor: '#dc2626',
      badgeColor: '#991b1b',
      statusLabel: 'Expired',
    };
  }

  if (daysUntilExpiration <= 2) {
    return {
      borderColor: '#f97316',
      badgeColor: '#c2410c',
      statusLabel: 'Soon',
    };
  }

  if (daysUntilExpiration <= 4) {
    return {
      borderColor: '#eab308',
      badgeColor: '#a16207',
      statusLabel: 'Fresh',
    };
  }

  return {
    borderColor: '#16a34a',
    badgeColor: '#166534',
    statusLabel: 'Good',
  };
}

function parseQuantity(quantity = '') {
  const trimmedQuantity = quantity.trim();
  const match = trimmedQuantity.match(/^(-?\d+(?:\.\d+)?)(?:\s*(.*))?$/);

  if (!match) {
    return null;
  }

  return {
    value: Number.parseFloat(match[1]),
    unit: match[2]?.trim() ?? '',
  };
}

function formatQuantity(value, unit = '') {
  const formattedValue = Number.isInteger(value) ? `${value}` : `${value}`.replace(/\.0+$/, '');
  return unit ? `${formattedValue} ${unit}` : formattedValue;
}

function splitQuantity(quantity = '') {
  const parsedQuantity = parseQuantity(quantity);

  if (!parsedQuantity || parsedQuantity.value <= 0) {
    return null;
  }

  const { value, unit } = parsedQuantity;

  if (value > 1 && !Number.isInteger(value)) {
    return null;
  }

  if (value > 1) {
    const splitPortion = Math.floor(value / 2);
    const remainingPortion = value - splitPortion;

    return {
      originalQuantity: formatQuantity(remainingPortion, unit),
      splitQuantity: formatQuantity(splitPortion, unit),
    };
  }

  const halfValue = value / 2;

  return {
    originalQuantity: formatQuantity(halfValue, unit),
    splitQuantity: formatQuantity(halfValue, unit),
  };
}

function FoodItem({ id, containerKey, name, quantity, expirationDate }) {
  const dispatch = useDispatch();
  const { userLoggedIn } = useAuth();
  const dragStartedRef = useRef(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [formData, setFormData] = useState({
    name,
    quantity,
    expirationDate,
  });

  const imageSource = useMemo(() => getBestFoodImage(name), [name]);
  const daysUntilExpiration = useMemo(() => getDaysUntilExpiration(expirationDate), [expirationDate]);
  const expirationStyle = useMemo(() => getExpirationStyle(daysUntilExpiration), [daysUntilExpiration]);
  const showStatusBadge =
    expirationStyle.statusLabel === 'Expired' || expirationStyle.statusLabel === 'Soon';

  const openEditor = () => {
    if (dragStartedRef.current) {
      return;
    }

    setFormData({
      name,
      quantity,
      expirationDate,
    });
    setSaveError('');
    setIsEditing(true);
  };

  const closeEditor = () => {
    if (isSaving) {
      return;
    }

    setIsEditing(false);
    setSaveError('');
  };

  const handleFieldChange = (event) => {
    const { name: fieldName, value } = event.target;

    setFormData((current) => ({
      ...current,
      [fieldName]: value,
    }));
  };

  const handleDelete = async () => {
    if (!userLoggedIn || !id || !containerKey) {
      setSaveError('Unable to delete this item right now.');
      return;
    }

    try {
      setIsSaving(true);
      setSaveError('');
      await deleteFoodItemFromDatabase(userLoggedIn, containerKey, id);
      dispatch({
        type: 'REMOVE_ITEM',
        payload: {
          containerKey,
          itemId: id,
        },
      });
      setIsEditing(false);
    } catch (error) {
      setSaveError('Unable to delete this item right now.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSplit = async () => {
    if (!userLoggedIn || !id || !containerKey) {
      setSaveError('Unable to split this item right now.');
      return;
    }

    const splitResult = splitQuantity(formData.quantity);

    if (!splitResult) {
      setSaveError('Split needs a positive numeric quantity. Counts above 1 must be whole numbers.');
      return;
    }

    const originalItemUpdates = {
      name: formData.name.trim(),
      quantity: splitResult.originalQuantity,
      expirationDate: formData.expirationDate,
    };
    const splitItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      containerKey,
      name: formData.name.trim(),
      quantity: splitResult.splitQuantity,
      expirationDate: formData.expirationDate,
    };

    try {
      setIsSaving(true);
      setSaveError('');
      await splitFoodItemInDatabase(
        userLoggedIn,
        containerKey,
        id,
        originalItemUpdates,
        {
          id: splitItem.id,
          name: splitItem.name,
          quantity: splitItem.quantity,
          expirationDate: splitItem.expirationDate,
        }
      );

      dispatch({
        type: 'UPDATE_ITEM',
        payload: {
          containerKey,
          itemId: id,
          updates: originalItemUpdates,
        },
      });
      dispatch({
        type: 'ADD_ITEM',
        payload: {
          containerKey,
          item: splitItem,
        },
      });

      setFormData((current) => ({
        ...current,
        quantity: splitResult.originalQuantity,
      }));
      setIsEditing(false);
    } catch (error) {
      setSaveError('Unable to split this item right now.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!userLoggedIn || !id || !containerKey) {
      setSaveError('Unable to edit this item right now.');
      return;
    }

    if (!formData.name.trim() || !formData.quantity.trim() || !formData.expirationDate) {
      setSaveError('Name, quantity, and expiration date are required.');
      return;
    }

    if (shouldDeleteForQuantity(formData.quantity)) {
      await handleDelete();
      return;
    }

    const updates = {
      name: formData.name.trim(),
      quantity: formData.quantity.trim(),
      expirationDate: getAdjustedExpirationDate(
        containerKey,
        containerKey,
        formData.expirationDate
      ),
    };

    try {
      setIsSaving(true);
      setSaveError('');
      await updateFoodItemInDatabase(userLoggedIn, containerKey, id, updates);
      dispatch({
        type: 'UPDATE_ITEM',
        payload: {
          containerKey,
          itemId: id,
          updates,
        },
      });
      setIsEditing(false);
    } catch (error) {
      setSaveError('Unable to save changes right now.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDragStart = (event) => {
    dragStartedRef.current = true;

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        id,
        sourceContainerKey: containerKey,
        name,
        quantity,
        expirationDate,
      })
    );
  };

  const handleDragEnd = () => {
    window.setTimeout(() => {
      dragStartedRef.current = false;
    }, 0);
  };

  return (
    <>
      <li
        draggable
        className={`relative h-14 w-14 cursor-pointer overflow-hidden rounded-lg transition-transform duration-200 ease-in-out ${
          isHovered ? 'translate-y-[-2px] shadow-lg' : 'shadow-sm'
        }`}
        style={{ border: `2px solid ${expirationStyle.borderColor}` }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={openEditor}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        title={`${name} • ${quantity} • Expires ${expirationDate || 'N/A'}`}
      >
        {showStatusBadge && (
          <span
            className="absolute left-0.5 top-0.5 z-10 rounded-full px-1 py-0 text-[9px] font-bold text-white"
            style={{ backgroundColor: expirationStyle.badgeColor }}
          >
            !
          </span>
        )}

        <div className="flex h-full items-center justify-center bg-gradient-to-b from-white to-gray-100 p-1">
          {imageSource ? (
            <img
              src={imageSource}
              alt={name}
              className="h-8 w-8 object-contain drop-shadow-sm"
            />
          ) : (
            <div className="px-1 text-center text-[9px] font-semibold leading-tight text-gray-800">
              {name}
            </div>
          )}
        </div>

        <div className="absolute bottom-0.5 right-0.5 max-w-[80%] truncate rounded bg-gray-800 px-1 py-0 text-[8px] font-bold text-white">
          {quantity}
        </div>

        {isHovered && (
          <div className="absolute inset-0 flex flex-col justify-end gap-0.5 bg-gradient-to-t from-gray-900/95 to-gray-900/35 p-1 text-[8px] text-white">
            <div className="truncate font-semibold">{name}</div>
            <div className="truncate">{quantity}</div>
            <div className="truncate">{expirationDate || 'No date'}</div>
          </div>
        )}
      </li>

      {isEditing && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/45 p-4"
          onClick={closeEditor}
        >
          <form
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
            onSubmit={handleSave}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Edit food item</h3>

            <label className="mb-3 block text-sm font-medium text-slate-700">
              Name
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFieldChange}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>

            <label className="mb-3 block text-sm font-medium text-slate-700">
              Quantity
              <input
                type="text"
                name="quantity"
                value={formData.quantity}
                onChange={handleFieldChange}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>

            <label className="mb-4 block text-sm font-medium text-slate-700">
              Expiration date
              <input
                type="date"
                name="expirationDate"
                value={formData.expirationDate}
                onChange={handleFieldChange}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>

            {saveError && <p className="mb-4 text-sm text-red-700">{saveError}</p>}

            <div className="flex flex-wrap justify-between gap-3">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isSaving}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={handleSplit}
                  disabled={isSaving}
                  className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white"
                >
                  Split
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeEditor}
                  disabled={isSaving}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default FoodItem;
